/**
 * MissionReviewModal — opens whenever a HQ mission reaches the
 * `awaiting_approval` state. It surfaces:
 *   • the artifact Aria produced (email body, memo, script…)
 *   • the agents involved + their thoughts (compact summary)
 *   • Approve · Reject · Request changes actions
 *
 * Whichever button is clicked, we POST the verdict to the backend.
 * Aria's contextual chat follow-up is generated server-side and will
 * land in the cockpit chat on the next poll (~6s).
 */
import React, { useState } from 'react';
import {
  CheckCircle2, X, Edit3, Send, Loader2, Mail, FileText, Users,
  Sparkles, AlertOctagon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ARTIFACT_ICON = {
  email: Mail,
  memo: FileText,
  script: FileText,
  post: Sparkles,
  plan: FileText,
};

export default function MissionReviewModal({ mission, onClose, onDecided }) {
  const [busy, setBusy] = useState(false);
  const [showRevise, setShowRevise] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [feedback, setFeedback] = useState('');

  if (!mission) return null;
  const artifact = mission.artifact || {};
  const ArtIcon = ARTIFACT_ICON[artifact.kind] || FileText;
  const cards = mission.trello_cards || [];

  const submit = async (verdict, feedbackText = '') => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(
        `${API}/api/hq/${mission.monetize_id}/missions/${mission.mission_id}/decision`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ verdict, feedback: feedbackText }),
        },
      );
      if (r.ok) {
        onDecided && onDecided(verdict, await r.json());
      }
    } catch {}
    setBusy(false);
    onClose && onClose();
  };

  return (
    <div
      data-testid="mission-review-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(8, 8, 14, 0.74)',
        backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        animation: 'fadeIn 0.18s ease-out',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose && onClose();
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 720, maxHeight: '88vh',
          background: '#13131A',
          color: '#F5F5F7',
          border: '1px solid rgba(94, 234, 212, 0.22)',
          borderRadius: 18,
          boxShadow: '0 30px 80px rgba(0,0,0,0.45), 0 0 80px rgba(94,234,212,0.08)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 22px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 36, height: 36, flexShrink: 0,
            background: 'rgba(94, 234, 212, 0.12)',
            border: '1px solid rgba(94, 234, 212, 0.4)',
            color: '#5EEAD4',
            borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ArtIcon size={17} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: 9, fontWeight: 800, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: '#5EEAD4',
            }}>
              Aria · awaiting your verdict
            </div>
            <div style={{
              fontSize: 16, fontWeight: 800, marginTop: 2,
              letterSpacing: '-0.01em', color: '#F5F5F7',
            }}>
              {mission.title}
            </div>
          </div>
          <button
            onClick={() => !busy && onClose && onClose()}
            data-testid="mission-modal-close"
            style={{
              width: 32, height: 32,
              background: 'transparent', color: 'rgba(255,255,255,0.5)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 8, cursor: busy ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px' }}>
          {/* Agents row */}
          {Array.isArray(mission.agents) && mission.agents.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              marginBottom: 14, flexWrap: 'wrap',
              fontSize: 10.5, color: 'rgba(255,255,255,0.45)',
              letterSpacing: '0.06em',
            }}>
              <Users size={11} />
              <span style={{ textTransform: 'uppercase', fontWeight: 700 }}>Worked on this:</span>
              {mission.agents.map((a) => (
                <span key={a} style={{
                  padding: '3px 9px', fontSize: 10, fontWeight: 700,
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 999,
                  color: '#F5F5F7',
                }}>{a}</span>
              ))}
            </div>
          )}

          {/* Artifact card — the deliverable */}
          <ArtifactPreview artifact={artifact} />

          {/* Trello cards (compact) */}
          {cards.length > 0 && (
            <details
              data-testid="mission-cards-details"
              style={{ marginTop: 16 }}
            >
              <summary style={{
                fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em',
                textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)',
                cursor: 'pointer', userSelect: 'none',
              }}>
                See what the team did in the Office ({cards.length} cards)
              </summary>
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {cards.map((c) => (
                  <div key={c.card_id} style={{
                    padding: '8px 10px',
                    background: 'rgba(255,255,255,0.03)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    borderRadius: 8,
                    display: 'flex', gap: 8, alignItems: 'flex-start',
                  }}>
                    <span style={{
                      fontSize: 9, fontWeight: 800, letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      padding: '2px 7px', borderRadius: 999,
                      background: laneTint(c.lane),
                      color: '#0A0A0B',
                      flexShrink: 0, marginTop: 1,
                    }}>{c.lane}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 11.5, color: '#F5F5F7', fontWeight: 600 }}>{c.title}</div>
                      <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.5)', marginTop: 2, lineHeight: 1.45 }}>
                        {c.author && <span style={{ color: '#5EEAD4', fontWeight: 700 }}>{c.author}: </span>}
                        {c.summary}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Inline feedback panels for revise / reject */}
        {(showRevise || showReject) && (
          <div style={{
            padding: '14px 22px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            background: 'rgba(255,255,255,0.02)',
          }}>
            <div style={{
              fontSize: 10, fontWeight: 700, letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: showRevise ? '#FBBF24' : '#FF6B6B',
              marginBottom: 8,
            }}>
              {showRevise ? 'What should change?' : 'Why is this not the right draft?'}
            </div>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={showRevise
                ? 'Length? CTA? Tone? Audience? Be specific so v2 hits.'
                : 'Wrong angle? Wrong tone? Wrong audience? Tell Aria what missed.'}
              data-testid="mission-feedback-input"
              rows={3}
              style={{
                width: '100%', resize: 'vertical',
                background: '#0A0A0B', color: '#F5F5F7',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8, padding: 10,
                fontFamily: 'inherit', fontSize: 12.5, lineHeight: 1.5,
                outline: 'none',
              }}
              autoFocus
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowRevise(false); setShowReject(false); setFeedback(''); }}
                disabled={busy}
                style={ghostBtn}
              >
                Cancel
              </button>
              <button
                onClick={() => submit(showRevise ? 'revision_requested' : 'rejected', feedback)}
                disabled={busy || !feedback.trim()}
                data-testid="mission-feedback-submit"
                style={{
                  ...primaryBtn,
                  background: showRevise ? '#FBBF24' : '#FF6B6B',
                  color: '#0A0A0B',
                  opacity: busy || !feedback.trim() ? 0.5 : 1,
                  cursor: busy || !feedback.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {busy ? <Loader2 size={13} style={{ animation: 'spin 0.7s linear infinite' }} /> : <Send size={13} />}
                {showRevise ? 'Request changes' : 'Send rejection'}
              </button>
            </div>
          </div>
        )}

        {/* Action bar */}
        {!showRevise && !showReject && (
          <div style={{
            padding: '14px 22px',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex', gap: 8, justifyContent: 'flex-end',
            background: 'rgba(255,255,255,0.015)',
          }}>
            <button
              onClick={() => setShowReject(true)}
              disabled={busy}
              data-testid="mission-reject-btn"
              style={{
                ...ghostBtn,
                borderColor: 'rgba(255,107,107,0.35)',
                color: '#FF6B6B',
              }}
            >
              <AlertOctagon size={13} /> Reject
            </button>
            <button
              onClick={() => setShowRevise(true)}
              disabled={busy}
              data-testid="mission-revise-btn"
              style={{
                ...ghostBtn,
                borderColor: 'rgba(251,191,36,0.35)',
                color: '#FBBF24',
              }}
            >
              <Edit3 size={13} /> Request changes
            </button>
            <button
              onClick={() => submit('approved')}
              disabled={busy}
              data-testid="mission-approve-btn"
              style={{
                ...primaryBtn,
                background: '#22C55E',
                color: '#0A0A0B',
                opacity: busy ? 0.5 : 1,
                cursor: busy ? 'not-allowed' : 'pointer',
              }}
            >
              {busy ? (
                <Loader2 size={13} style={{ animation: 'spin 0.7s linear infinite' }} />
              ) : (
                <CheckCircle2 size={13} />
              )}
              Approve & lock
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.98); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

function ArtifactPreview({ artifact }) {
  if (!artifact || (!artifact.body && !artifact.title)) {
    return (
      <div style={{
        padding: 14, background: 'rgba(255,255,255,0.03)',
        border: '1px dashed rgba(255,255,255,0.1)',
        borderRadius: 12, color: 'rgba(255,255,255,0.5)', fontSize: 12, fontStyle: 'italic',
      }}>
        Aria couldn't produce a deliverable. Try rephrasing the request.
      </div>
    );
  }
  const isEmail = artifact.kind === 'email';

  return (
    <div
      data-testid="mission-artifact"
      style={{
        padding: 16,
        background: '#0A0A0B',
        border: '1px solid rgba(94, 234, 212, 0.22)',
        borderRadius: 12,
      }}
    >
      {isEmail ? (
        <>
          {artifact.recipient && (
            <Row label="To" value={artifact.recipient} />
          )}
          {artifact.subject && (
            <Row label="Subject" value={artifact.subject} />
          )}
          <div style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid rgba(255,255,255,0.06)',
            whiteSpace: 'pre-wrap',
            fontSize: 13, lineHeight: 1.6,
            color: '#F5F5F7',
          }}>
            {artifact.body}
          </div>
        </>
      ) : (
        <>
          {artifact.title && (
            <div style={{ fontSize: 13.5, fontWeight: 800, color: '#F5F5F7', marginBottom: 8 }}>
              {artifact.title}
            </div>
          )}
          <div style={{
            whiteSpace: 'pre-wrap',
            fontSize: 13, lineHeight: 1.6,
            color: '#F5F5F7',
          }}>
            {artifact.body}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 12, marginBottom: 4 }}>
      <span style={{
        fontSize: 10, fontWeight: 700, letterSpacing: '0.12em',
        textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)',
        minWidth: 56, paddingTop: 2,
      }}>{label}</span>
      <span style={{ color: '#F5F5F7', flex: 1 }}>{value}</span>
    </div>
  );
}

function laneTint(lane) {
  switch (lane) {
    case 'brainstorm': return '#A855F7';
    case 'doing':      return '#06B6D4';
    case 'done':       return '#22C55E';
    case 'blocked':    return '#FBBF24';
    default:           return '#94A3B8';
  }
}

const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', fontSize: 11.5, fontWeight: 800,
  border: 'none', borderRadius: 9,
  fontFamily: 'inherit', letterSpacing: '0.02em',
  cursor: 'pointer',
};

const ghostBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', fontSize: 11.5, fontWeight: 700,
  background: 'transparent', color: 'rgba(255,255,255,0.65)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 9, fontFamily: 'inherit', letterSpacing: '0.02em',
  cursor: 'pointer',
};
